import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Camera,
  SwitchCamera,
  X,
  AlertCircle,
  Maximize2,
  Minimize2,
  Timer,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ModernCameraProps {
  onCapture: (imageData: string) => void;
  onClose?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  overlayGuide?: {
    width: string;
    height: string;
    shape?: "rectangle" | "circle" | "oval";
  };
  instructions?: {
    title: string;
    description: string;
    example?: string;
  };
  className?: string;
  autoCapture?: boolean;
  captureDelay?: number;
  disabled?: boolean;
}

export function ModernCamera({
  onCapture,
  onClose,
  isFullscreen = false,
  onToggleFullscreen,
  overlayGuide,
  instructions,
  className,
  autoCapture = false,
  captureDelay = 3,
  disabled = false,
}: ModernCameraProps) {
  const isMobile = useIsMobile();
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [streamReady, setStreamReady] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const captureInProgressRef = useRef<boolean>(false);

  // Forward declarations
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !streamReady || disabled) {
      console.warn(
        "Cannot capture: missing video or canvas ref, stream not ready, or disabled",
      );
      return;
    }

    // Use ref-based debouncing to prevent multiple rapid calls
    if (captureInProgressRef.current) {
      console.log("Capture already in progress, ignoring duplicate call");
      return;
    }

    captureInProgressRef.current = true;
    setIsCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      if (!ctx) throw new Error("Cannot get canvas context");

      // Set canvas size to video size
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      // Draw video frame to canvas
      if (facingMode === "user") {
        // Flip horizontally for front camera
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }

      // Convert to image data with maximum quality
      const imageData = canvas.toDataURL("image/jpeg", 1.0);
      onCapture(imageData);
    } catch (error) {
      console.error("Capture failed:", error);
      setCameraError("Failed to capture photo. Please try again.");
    } finally {
      setIsCapturing(false);
      captureInProgressRef.current = false;
    }
  }, [facingMode, onCapture, streamReady, disabled]);

  const startCountdown = useCallback(() => {
    if (countdownRef.current) clearTimeout(countdownRef.current);

    let count = captureDelay;
    setCountdown(count);

    const timer = setInterval(() => {
      count -= 1;
      setCountdown(count);
      if (count <= 0) {
        clearInterval(timer);
        setCountdown(null);
        capturePhoto();
      }
    }, 1000);

    countdownRef.current = timer as any;
  }, [captureDelay, capturePhoto]);

  // Handle capture action
  const handleCapture = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      console.log("Capture handler triggered", {
        autoCapture,
        countdown,
        isCapturing,
        streamReady,
        captureInProgress: captureInProgressRef.current,
        disabled:
          isCapturing ||
          !!countdown ||
          !streamReady ||
          captureInProgressRef.current,
      });

      if (
        isCapturing ||
        countdown ||
        !streamReady ||
        captureInProgressRef.current
      ) {
        console.log("Capture blocked - button should be disabled");
        return;
      }

      if (autoCapture && !countdown) {
        console.log("Starting countdown...");
        startCountdown();
      } else if (!autoCapture && !countdown) {
        console.log("Taking photo directly...");
        capturePhoto();
      }
    },
    [
      autoCapture,
      countdown,
      startCountdown,
      capturePhoto,
      isCapturing,
      streamReady,
    ],
  );

  // Start camera function with enhanced stream management
  const startCamera = useCallback(async () => {
    console.log(
      "Starting camera... Fullscreen:",
      isFullscreen,
      "FacingMode:",
      facingMode,
    );
    setCameraError(null);
    setStreamReady(false);

    try {
      // Always stop existing stream when restarting
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Check browser support
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera not supported in this browser");
      }

      // Enhanced constraints for highest quality
      const constraints = [
        {
          video: {
            facingMode: facingMode,
            width: { ideal: 2560, min: 1920 },
            height: { ideal: 1440, min: 1080 },
            frameRate: { ideal: 30 },
          },
          audio: false,
        },
        {
          video: {
            facingMode: facingMode,
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
          },
          audio: false,
        },
        {
          video: { facingMode: facingMode },
          audio: false,
        },
        {
          video: true,
          audio: false,
        },
      ];

      let stream = null;
      let lastError = null;

      // Try constraints in order of preference
      for (let i = 0; i < constraints.length; i++) {
        try {
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(
              () => reject(new Error("Camera request timeout")),
              10000,
            );
          });

          const streamPromise = navigator.mediaDevices.getUserMedia(
            constraints[i],
          );
          stream = (await Promise.race([
            streamPromise,
            timeoutPromise,
          ])) as MediaStream;
          break;
        } catch (error) {
          lastError = error;
          if (i === constraints.length - 1) {
            throw lastError;
          }
        }
      }

      if (!stream) {
        throw new Error("Failed to obtain camera stream");
      }

      streamRef.current = stream;

      // Set up video with enhanced mobile support
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.autoplay = true;
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;

        // Wait for metadata to load
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(
            () => reject(new Error("Video metadata timeout")),
            5000,
          );

          videoRef.current!.onloadedmetadata = () => {
            clearTimeout(timeout);
            resolve(void 0);
          };

          videoRef.current!.onerror = (error) => {
            clearTimeout(timeout);
            reject(error);
          };
        });

        // Force play for mobile
        try {
          await videoRef.current.play();
          setStreamReady(true);
        } catch (playError) {
          console.warn("Autoplay failed, but continuing:", playError);
          setStreamReady(true);
        }
      }

      setHasStarted(true);
    } catch (error: any) {
      console.error("Camera error:", error);
      let message = "Failed to access camera. ";

      if (error.name === "NotAllowedError") {
        message =
          "Camera permission denied. Please allow camera access and try again.";
      } else if (error.name === "NotFoundError") {
        message = "No camera found. Please connect a camera and try again.";
      } else if (error.name === "NotReadableError") {
        message = "Camera is in use by another application.";
      } else if (error.message?.includes("timeout")) {
        message = "Camera request timed out. Please try again.";
      } else {
        message += error.message || "Please check your camera settings.";
      }

      setCameraError(message);
      setStreamReady(false);
    }
  }, [facingMode, isFullscreen]);

  // Flip camera
  const flipCamera = useCallback(() => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  }, []);

  // Handle facing mode changes - restart camera when facing mode changes
  useEffect(() => {
    if (hasStarted) {
      startCamera();
    }
  }, [facingMode, hasStarted, startCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (countdownRef.current) {
        clearTimeout(countdownRef.current);
      }
    };
  }, []);

  // If not started yet, show start button
  if (!hasStarted && !cameraError) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center">
          <Camera className="w-16 h-16 mx-auto mb-4 text-blue-500" />
          <h3 className="text-xl font-semibold mb-3">Ready to Take Photos</h3>
          <p className="text-gray-600 mb-6">Click below to start your camera</p>
          <div className="w-full space-y-2">
            <Button
              onClick={startCamera}
              size="lg"
              className="w-full"
              type="button"
            >
              <Camera className="w-5 h-5 mr-2" />
              Start Camera
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (cameraError) {
    return (
      <Card className={className}>
        <CardContent className="p-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h3 className="text-lg font-semibold mb-2">Camera Error</h3>
          <p className="text-gray-600 mb-4 text-sm">{cameraError}</p>
          <div className="space-y-2">
            <Button onClick={startCamera} className="w-full" type="button">
              <Camera className="w-4 h-4 mr-2" />
              Try Again
            </Button>
            <Button
              onClick={() => {
                setHasStarted(false);
                setCameraError(null);
                setStreamReady(false);
              }}
              variant="outline"
              className="w-full"
            >
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fullscreen mode - truly fullscreen with fixed UI
  if (isFullscreen) {
    return (
      <div
        ref={(el) => {
          if (el && isFullscreen) {
            const isMobile = /Mobi|Android/i.test(navigator.userAgent);

            if (isMobile) {
              // Only request fullscreen on mobile devices
              if (el.requestFullscreen) {
                el.requestFullscreen().catch(() => {});
              } else if ((el as any).webkitRequestFullscreen) {
                (el as any).webkitRequestFullscreen();
              } else if ((el as any).msRequestFullscreen) {
                (el as any).msRequestFullscreen();
              }
            }
          }
        }}
        className="fixed inset-0 bg-black flex flex-col overflow-hidden w-[100dvw] h-[100dvh]"
        style={{ zIndex: 999999 }}
      >
        {/* Top Status Bar */}
        <div className="absolute top-0 left-0 right-0 z-30 safe-area-top">
          <div className="bg-gradient-to-b from-black/90 via-black/60 to-transparent backdrop-blur-sm">
            <div className="flex items-center justify-between px-4 py-3">
              {/* Left: Instructions */}
              <div className="flex-1 min-w-0">
                {instructions ? (
                  <div className="max-w-sm">
                    <h3 className="font-semibold text-sm text-white truncate leading-tight">
                      {instructions.title}
                    </h3>
                    <p className="text-xs text-gray-300 truncate mt-0.5 leading-tight">
                      {instructions.description}
                    </p>
                  </div>
                ) : (
                  <div className="text-white">
                    <p className="text-sm font-medium">Camera</p>
                  </div>
                )}
              </div>

              {/* Right: Action buttons */}
              <div className="flex gap-2 ml-4 shrink-0">
                {onToggleFullscreen && (
                  <Button
                    onClick={onToggleFullscreen}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20 active:bg-white/30 bg-black/40 backdrop-blur-sm h-10 w-10 p-0 rounded-full border border-white/20 transition-all duration-200"
                  >
                    <Minimize2 className="w-4 h-4" />
                  </Button>
                )}
                {onClose && (
                  <Button
                    onClick={onClose}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20 active:bg-white/30 bg-black/40 backdrop-blur-sm h-10 w-10 p-0 rounded-full border border-white/20 transition-all duration-200"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              "max-w-full max-h-full object-contain",
              facingMode === "user" && "scale-x-[-1]",
            )}
          />
        </div>

        {/* Example Image Overlay */}
        {instructions?.example && !countdown && streamReady && (
          <div className="absolute top-20 right-4 z-20 animate-in fade-in duration-500">
            <div className="bg-black/90 rounded-2xl p-3 backdrop-blur-lg border border-white/30 shadow-2xl">
              <img
                src={instructions.example}
                alt="Example position"
                className="w-24 h-24 object-cover rounded-xl border-2 border-white/50 shadow-lg"
              />
              <p className="text-white text-xs text-center mt-2 font-medium opacity-90">
                Example
              </p>
            </div>
          </div>
        )}

        {/* Countdown Overlay */}
        {countdown && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-30">
            <div className="text-center">
              <div className="relative">
                <div className="text-white text-9xl font-bold animate-pulse mb-4 drop-shadow-2xl">
                  {countdown}
                </div>
                <div className="absolute inset-0 text-blue-400 text-9xl font-bold animate-ping opacity-30">
                  {countdown}
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 border border-white/30">
                <p className="text-white text-lg font-medium">Get ready...</p>
              </div>
            </div>
          </div>
        )}

        {/* Overlay Guide */}
        {overlayGuide &&
          !countdown &&
          streamReady &&
          /Mobi|Android/i.test(navigator.userAgent) && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div
                className={cn(
                  "relative border-4 border-white/80 shadow-2xl transition-all duration-300",
                  overlayGuide.shape === "circle"
                    ? "rounded-full"
                    : overlayGuide.shape === "oval"
                      ? "rounded-full"
                      : "rounded-3xl",
                )}
                style={{
                  width: `calc(${overlayGuide.width} * 1.2)`,
                  height: `calc(${overlayGuide.height} * 1.2)`,
                  aspectRatio:
                    overlayGuide.shape === "oval" ? "3/4" : undefined,
                  boxShadow:
                    "0 0 0 9999px rgba(0, 0, 0, 0.55), inset 0 0 0 3px rgba(255, 255, 255, 0.25)",
                }}
              >
                {/* Animated corner indicators */}
                {overlayGuide.shape === "rectangle" && (
                  <>
                    <div className="absolute -top-2 -left-2 w-6 h-6 border-l-4 border-t-4 border-white/90 rounded-tl-lg animate-pulse"></div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 border-r-4 border-t-4 border-white/90 rounded-tr-lg animate-pulse"></div>
                    <div className="absolute -bottom-2 -left-2 w-6 h-6 border-l-4 border-b-4 border-white/90 rounded-bl-lg animate-pulse"></div>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 border-r-4 border-b-4 border-white/90 rounded-br-lg animate-pulse"></div>
                  </>
                )}
              </div>
            </div>
          )}

        {/* Loading Overlay */}
        {!streamReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-40">
            <div className="text-white text-center">
              <div className="relative mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/30 border-t-white mx-auto"></div>
                <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-2 border-white/20 mx-auto"></div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4 border border-white/20">
                <p className="text-lg font-medium mb-1">Loading camera...</p>
                <p className="text-sm text-gray-300">Please wait a moment</p>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-50">
          <div className="bg-gradient-to-t from-black/90 via-black/60 to-transparent backdrop-blur-sm">
            <div className="flex items-center justify-center px-6 py-6 min-h-[100px]">
              <div className="flex items-center justify-between w-full max-w-md">
                {/* Left: Flip Camera */}
                <Button
                  onClick={flipCamera}
                  variant="ghost"
                  size="lg"
                  className="text-white hover:bg-white/20 active:bg-white/30 bg-black/40 backdrop-blur-sm w-12 h-12 rounded-full p-0 border border-white/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  disabled={!streamReady}
                >
                  <SwitchCamera className="w-5 h-5" />
                </Button>

                {/* Center: Capture Button */}
                <div className="relative flex-shrink-0 mx-4">
                  {/* Capture Button - Always clickable for debugging */}
                  <button
                    onClick={(e) => {
                      console.log("CAPTURE BUTTON CLICKED!", {
                        isCapturing,
                        countdown,
                        streamReady,
                        disabled: isCapturing || !!countdown || !streamReady,
                      });

                      // Force capture even if disabled for debugging
                      if (streamReady) {
                        handleCapture(e);
                      } else {
                        console.log("Stream not ready, cannot capture");
                      }
                    }}
                    className={cn(
                      "bg-white hover:bg-gray-100 active:bg-gray-200 text-black w-16 h-16 sm:w-18 sm:h-18 rounded-full flex items-center justify-center transition-all duration-200 shadow-2xl border-4 border-white/50",
                      (isCapturing || countdown) && "animate-pulse",
                      (isCapturing || !!countdown || !streamReady) &&
                        "opacity-50 cursor-not-allowed",
                    )}
                    type="button"
                    style={{
                      touchAction: "manipulation",
                      pointerEvents: "auto",
                      zIndex: 9999,
                      position: "relative",
                    }}
                  >
                    {isCapturing ? (
                      <div className="animate-spin rounded-full h-6 w-6 sm:h-7 sm:w-7 border-2 border-gray-600 border-t-transparent"></div>
                    ) : autoCapture && !countdown ? (
                      <Timer className="w-7 h-7 sm:w-8 sm:h-8" />
                    ) : (
                      <Camera className="w-7 h-7 sm:w-8 sm:h-8" />
                    )}
                  </button>

                  {/* Capture button ring animation */}
                  {streamReady && !countdown && (
                    <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping pointer-events-none"></div>
                  )}
                </div>

                {/* Right: Status Indicator */}
                <div className="w-12 h-12 flex flex-col items-center justify-center flex-shrink-0">
                  {streamReady ? (
                    <div className="relative">
                      <div className="w-3 h-3 bg-green-400 rounded-full shadow-lg"></div>
                      <div className="absolute inset-0 w-3 h-3 bg-green-400 rounded-full animate-ping opacity-30"></div>
                    </div>
                  ) : (
                    <div className="w-3 h-3 bg-red-400 rounded-full opacity-60 animate-pulse"></div>
                  )}
                  {/* Debug info */}
                  <div className="text-white text-xs mt-1">
                    {isCapturing
                      ? "CAP"
                      : countdown
                        ? "CD"
                        : streamReady
                          ? "RDY"
                          : "WAIT"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  // Regular camera view
  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div
          className={cn(
            "bg-gray-100 rounded-lg overflow-hidden relative",
            isMobile ? "aspect-[3/4]" : "aspect-video",
          )}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              "w-full h-full object-cover",
              facingMode === "user" && "scale-x-[-1]",
            )}
          />

          {/* Demo Image Overlay */}
          {instructions?.example && !countdown && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-black/80 rounded-lg p-2 backdrop-blur-sm border border-white/20">
                <img
                  src={instructions.example}
                  alt="Example position"
                  className="w-16 h-16 object-cover rounded border border-white/50"
                />
                <p className="text-white text-xs text-center mt-1 font-medium">
                  Example
                </p>
              </div>
            </div>
          )}

          {/* Countdown */}
          {countdown && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-white text-6xl font-bold animate-pulse">
                {countdown}
              </div>
            </div>
          )}

          {/* Overlay Guide */}
          {overlayGuide && !countdown && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className={cn(
                  "border-2 border-white/70 flex items-center justify-center",
                  overlayGuide.shape === "circle"
                    ? "rounded-full"
                    : overlayGuide.shape === "oval"
                      ? "rounded-full"
                      : "rounded-lg",
                )}
                style={{
                  width: overlayGuide.width,
                  height: overlayGuide.height,
                  aspectRatio:
                    overlayGuide.shape === "oval" ? "3/4" : undefined,
                }}
              >
                <div className="text-center text-white text-sm">
                  <Camera className="w-6 h-6 mx-auto mb-1" />
                  <p>Position here</p>
                </div>
              </div>
            </div>
          )}

          {/* Fullscreen toggle */}
          {onToggleFullscreen && (
            <div className="absolute top-2 left-2 z-10">
              <Button
                onClick={onToggleFullscreen}
                variant="secondary"
                size="sm"
                className="bg-white/90 hover:bg-white shadow-lg"
                disabled={!streamReady}
              >
                <Maximize2 className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Stream loading indicator */}
          {!streamReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-white text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                <p className="text-sm">Loading camera...</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex gap-2 mt-4">
          <Button
            onClick={flipCamera}
            variant="outline"
            size="sm"
            disabled={!streamReady}
          >
            <SwitchCamera className="w-4 h-4 mr-2" />
            Flip
          </Button>

          <Button
            onClick={handleCapture}
            className="flex-1"
            disabled={isCapturing || !!countdown || !streamReady || disabled}
            type="button"
          >
            {autoCapture && !countdown ? (
              <>
                <Timer className="w-4 h-4 mr-2" />
                Start Timer ({captureDelay}s)
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" />
                {isCapturing ? "Capturing..." : "Take Photo"}
              </>
            )}
          </Button>

          {onToggleFullscreen && !isMobile && (
            <Button
              onClick={onToggleFullscreen}
              variant="outline"
              size="sm"
              disabled={!streamReady}
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </CardContent>
    </Card>
  );
}
